import classNames from 'classnames';

import { useChartTheme } from '@/components/charts/theme';
import { OVERVIEW_SCORE_LEGEND } from '@/routes/Client/Cage/pages/Health/HealthDashboard/components/LaksvelOverviewChart/laksvelOverviewColors';

import styles from './ParameterBar.module.scss';

export type LaksvelScorePercents = Record<(typeof OVERVIEW_SCORE_LEGEND)[number]['key'], number>;

/** The four Laksvel score shares in percent; score 0 is whatever the other three leave. */
export function laksvelScorePercents(
    score1: number,
    score2: number,
    score3: number
): LaksvelScorePercents {
    return { score0: Math.max(0, 100 - score1 - score2 - score3), score1, score2, score3 };
}

interface ParameterBarProps {
    /** Null when the day has no measurement: the track stays, with no score painted on it. */
    percents: LaksvelScorePercents | null;
    /** `sm` in the cage dashboard's compact rows, `md` in the health overview's cards. */
    height: 'sm' | 'md';
    /** How the bar sits in its row: full width, or flexing beside a label. */
    className?: string;
}

/** A health parameter's Laksvel score shares as one stacked bar, score 0 to 3 from the left. */
export function ParameterBar(props: ParameterBarProps) {
    const { percents, height, className } = props;
    const chartTheme = useChartTheme();

    return (
        <div
            className={classNames(styles.bar, styles[height], className)}
            data-score-bar={percents == null ? 'empty' : 'measured'}>
            {percents == null
                ? null
                : OVERVIEW_SCORE_LEGEND.map((segment) =>
                      percents[segment.key] > 0 ? (
                          <div
                              key={segment.key}
                              className={styles.segment}
                              style={{
                                  width: `${percents[segment.key]}%`,
                                  backgroundColor: chartTheme.color(segment.color),
                              }}
                          />
                      ) : null
                  )}
        </div>
    );
}
